import { Injectable } from '@angular/core';
import { Conversation } from '@elevenlabs/client';
import { ElevenLabsVoiceService } from './elevenlabs-voice.service';

@Injectable({ providedIn: 'root' })
export class ElevenLabsAgentService {
    private voiceConversation: any;
    private textConversation: any;
    private voiceConversationId = '';
    private textConversationId = '';

    constructor(private voice: ElevenLabsVoiceService) {}

    async start(
        partnerid: string,
        onMode: (mode: string) => void,
        onError: () => void,
        onMessage: (message: string, source: 'user' | 'ai', conversationId: string) => void,
        onToolResult: (toolName: string, result: unknown) => void,
    ): Promise<void> {
        try {
            await this.stopVoice();
            const signedUrl = await this.voice.createAgentSignedUrl(partnerid, 'voice');
            if (!signedUrl) throw new Error('Missing ElevenLabs agent signed URL');
            this.voiceConversation = await Conversation.startSession({
                signedUrl,
                onConnect: ({ conversationId }) => { this.voiceConversationId = conversationId; },
                onModeChange: ({ mode }) => onMode(mode),
                onError: () => onError(),
                onMessage: ({ message, source }) => onMessage(message, source, this.voiceConversationId),
                onAgentToolResponse: (event: any) => {
                    // ElevenLabs SDK versions do not consistently use the same
                    // property for an MCP tool response. Do not lose offer cards
                    // merely because the result is wrapped outside full_tool_result.
                    const result = event?.full_tool_result
                        ?? event?.tool_result
                        ?? event?.result
                        ?? event?.response;
                    const toolName = event?.tool_name ?? event?.toolName ?? event?.name;
                    if (toolName && result !== undefined && result !== null) {
                        onToolResult(String(toolName), result);
                    }
                },
                onDisconnect: () => { this.voiceConversation = undefined; this.voiceConversationId = ''; },
            });
        } catch (error) {
            console.error('[ElevenLabs agent] Session start failed', error);
            throw error;
        }
    }

    async sendText(
        partnerid: string,
        text: string,
        onError: () => void,
        onMessage: (message: string, source: 'user' | 'ai', conversationId: string) => void,
        onToolResult: (toolName: string, result: unknown) => void,
    ): Promise<void> {
        await this.startTextSession(partnerid, onError, onMessage, onToolResult);
        console.info('[ElevenLabs text] Sending user message', { length: String(text || '').length });
        this.textConversation.sendUserMessage(text);
    }

    async startTextSession(
        partnerid: string,
        onError: () => void,
        onMessage: (message: string, source: 'user' | 'ai', conversationId: string) => void,
        onToolResult: (toolName: string, result: unknown) => void,
    ): Promise<void> {
        if (!this.textConversation?.isOpen?.()) {
            console.info('[ElevenLabs text] Requesting signed session URL', { partnerid });
            const signedUrl = await this.voice.createAgentSignedUrl(partnerid, 'text');
            if (!signedUrl) throw new Error('Missing ElevenLabs text-agent signed URL');
            console.info('[ElevenLabs text] Signed session URL received; opening WebSocket session');
            this.textConversation = await Conversation.startSession({
                signedUrl,
                textOnly: true,
                onConnect: ({ conversationId }) => {
                    this.textConversationId = conversationId;
                    console.info('[ElevenLabs text] Session connected', { conversationId });
                },
                onError: () => {
                    console.error('[ElevenLabs text] Session error');
                    onError();
                },
                // The local user message is already rendered by ChatComponent.
                onMessage: ({ message, source }) => {
                    console.info('[ElevenLabs text] Message received', { source, length: String(message || '').length });
                    if (source === 'ai') onMessage(message, source, this.textConversationId);
                },
                onAgentToolResponse: (event: any) => {
                    const result = event?.full_tool_result ?? event?.tool_result ?? event?.result ?? event?.response;
                    const toolName = event?.tool_name ?? event?.toolName ?? event?.name;
                    if (toolName && result !== undefined && result !== null) onToolResult(String(toolName), result);
                },
                onDisconnect: () => {
                    console.warn('[ElevenLabs text] Session disconnected', { conversationId: this.textConversationId });
                    this.textConversation = undefined;
                    this.textConversationId = '';
                },
            });
        }
    }

    async stop(): Promise<void> {
        await Promise.all([this.stopVoice(), this.stopText()]);
    }

    async stopVoice(): Promise<void> {
        if (this.voiceConversation) await this.voiceConversation.endSession();
        this.voiceConversation = undefined;
        this.voiceConversationId = '';
    }

    async stopText(): Promise<void> {
        if (this.textConversation) await this.textConversation.endSession();
        this.textConversation = undefined;
        this.textConversationId = '';
    }
}
